import { Component } from '@theme/component';

/**
 * Product media hotspots component that handles interactive hotspots on product images.
 * Provides touch-friendly interactions and tooltip management.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement[]} hotspots - The hotspot button elements
 *
 * @extends {Component<Refs>}
 */
export class ProductMediaHotspots extends Component {
  /**
   * Currently active hotspot element
   * @type {HTMLElement|null}
   */
  #activeHotspot = null;

  /**
   * Static registry of all hotspot components for cross-component communication
   * @type {Set<ProductMediaHotspots>}
   */
  static #instances = new Set();

  connectedCallback() {
    super.connectedCallback();
    
    // Register this instance
    ProductMediaHotspots.#instances.add(this);
    
    // Add click handlers for touch devices
    this.addEventListener('click', this.#handleHotspotClick);
    
    // Add hover handlers for desktop devices
    this.addEventListener('mouseenter', this.#handleHotspotMouseEnter, true);
    this.addEventListener('mouseleave', this.#handleHotspotMouseLeave, true);
    
    // Add keyboard handlers for accessibility
    this.addEventListener('keydown', this.#handleHotspotKeydown);
    
    // Close tooltips when clicking outside
    document.addEventListener('click', this.#handleDocumentClick);
    
    // Handle keyboard navigation (general escape key)
    this.addEventListener('keydown', this.#handleKeydown);

    // Close hotspots when zoom dialog opens
    this.#setupZoomIntegration();
    
    // Reposition tooltips when window resizes
    window.addEventListener('resize', this.#handleResize);
    
    // Add specific event listeners to close buttons
    this.#setupCloseButtons();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    // Unregister this instance
    ProductMediaHotspots.#instances.delete(this);
    
    // Clean up close button event listeners
    const closeButtons = this.querySelectorAll('.hotspot__close');
    closeButtons.forEach(closeButton => {
      closeButton.removeEventListener('click', this.#handleCloseButtonClick);
      closeButton.removeEventListener('keydown', this.#handleCloseButtonKeydown);
    });
    
    document.removeEventListener('click', this.#handleDocumentClick);
    window.removeEventListener('resize', this.#handleResize);
    
    // Clear any pending resize timeout
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = null;
    }
  }

  /**
   * Handles window resize events to reposition tooltips
   */
  #handleResize = () => {
    if (this.#resizeTimeout) {
      clearTimeout(this.#resizeTimeout);
    }
    
    this.#resizeTimeout = setTimeout(() => {
      if (this.#activeHotspot) {
        this.#positionTooltip(this.#activeHotspot);
      }
    }, 100);
  };

  /** @type {number | null} */
  #resizeTimeout = null;

  /**
   * Sets up integration with zoom dialog functionality
   */
  #setupZoomIntegration() {
    // Find the zoom dialog in the same media gallery
    const mediaGallery = this.closest('media-gallery');
    if (!mediaGallery) return;

    const zoomDialog = mediaGallery.querySelector('zoom-dialog');
    if (!zoomDialog) return;

    // Listen for zoom dialog events
    const dialog = zoomDialog.querySelector('dialog');
    if (dialog) {
      dialog.addEventListener('open', () => {
        ProductMediaHotspots.closeAllTooltips();
      });
    }
  }

  /**
   * Handles hotspot click events
   * @param {Event} event - The click event
   */
  #handleHotspotClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    
    // On desktop devices with hover support, prevent clicking on hotspots
    // (tooltips should only show on hover)
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      // Still allow close button clicks
      const closeButton = event.target.closest('.hotspot__close');
      if (closeButton) {
        event.stopPropagation();
        event.preventDefault();
        
        // Find the hotspot that contains this close button
        const hotspot = closeButton.closest('.hotspot');
        if (hotspot && this.#activeHotspot === hotspot) {
          this.#closeActiveHotspot();
        }
      }
      return;
    }
    
    // Touch device behavior (original functionality)
    // Check if the click is on the close button or any element inside it (like SVG)
    const closeButton = event.target.closest('.hotspot__close');
    if (closeButton) {
      event.stopPropagation();
      event.preventDefault();
      
      // Find the hotspot that contains this close button
      const hotspot = closeButton.closest('.hotspot');
      if (hotspot && this.#activeHotspot === hotspot) {
        this.#closeActiveHotspot();
      }
      return;
    }
    
    const hotspot = event.target.closest('.hotspot');
    if (!(hotspot instanceof HTMLElement)) return;

    // Check if this is inside a zoomable container
    const zoomableContainer = hotspot.closest('.product-media-container--zoomable');
    if (zoomableContainer) {
      // Prevent zoom from triggering when clicking on hotspots
      event.stopPropagation();
    }

    event.stopPropagation();
    event.preventDefault();

    // Toggle active state
    if (this.#activeHotspot === hotspot) {
      this.#closeActiveHotspot();
    } else {
      this.#setActiveHotspot(hotspot);
    }
  };

  /**
   * Handles keyboard events on hotspots and close buttons
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleHotspotKeydown = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    
    // Check if the keyboard event is on the close button
    const closeButton = event.target.closest('.hotspot__close');
    if (closeButton && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopPropagation();
      
      // Find the hotspot that contains this close button
      const hotspot = closeButton.closest('.hotspot');
      if (hotspot && this.#activeHotspot === hotspot) {
        this.#closeActiveHotspot();
      }
      return;
    }
  };

  /**
   * Handles clicks outside hotspots to close tooltips
   * @param {Event} event - The click event
   */
  #handleDocumentClick = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    
    const isHotspotClick = event.target.closest('.hotspot');
    const isTooltipClick = event.target.closest('.hotspot__tooltip');
    
    if (!isHotspotClick && !isTooltipClick) {
      // Close all tooltips when clicking outside
      this.#closeAllTooltips();
    }
  };

  /**
   * Handles keyboard navigation
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.#closeAllTooltips();
    }
  };

  /**
   * Handles mouse enter events for hover functionality on desktop
   * @param {MouseEvent} event - The mouse enter event
   */
  #handleHotspotMouseEnter = (event) => {
    // Only handle hover on devices that support hover
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return;
    }

    if (!(event.target instanceof HTMLElement)) return;
    
    const hotspot = event.target.closest('.hotspot');
    if (!(hotspot instanceof HTMLElement)) return;

    // Don't interfere with already pinned hotspots
    if (this.#activeHotspot === hotspot) return;

    // Position tooltip for hover
    this.#positionTooltip(hotspot);
  };

  /**
   * Handles mouse leave events for hover functionality on desktop
   * @param {MouseEvent} event - The mouse leave event
   */
  #handleHotspotMouseLeave = (event) => {
    // Only handle hover on devices that support hover
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return;
    }

    if (!(event.target instanceof HTMLElement)) return;
    
    const hotspot = event.target.closest('.hotspot');
    if (!(hotspot instanceof HTMLElement)) return;

    // Don't close pinned hotspots on mouse leave
    if (this.#activeHotspot === hotspot) return;

    // The CSS will handle hiding the tooltip on mouse leave
    // We don't need to do anything here since we're using CSS :hover
  };

  /**
   * Sets the active hotspot and shows its tooltip
   * @param {HTMLElement} hotspot - The hotspot element to activate
   */
  #setActiveHotspot(hotspot) {
    // Close all tooltips across all instances
    this.#closeAllTooltips();
    
    // Calculate and apply optimal tooltip position
    this.#positionTooltip(hotspot);
    
    this.#activeHotspot = hotspot;
    hotspot.classList.add('active');
    hotspot.setAttribute('aria-expanded', 'true');
  }

  /**
   * Calculates and applies the optimal tooltip position based on hotspot location
   * @param {HTMLElement} hotspot - The hotspot element
   */
  #positionTooltip(hotspot) {
    // Reset positioning classes
    hotspot.classList.remove('tooltip-below', 'tooltip-above', 'tooltip-left', 'tooltip-right');
    
    const container = this.closest('.product-media-container');
    if (!container) return;
    
    const hotspotRect = hotspot.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const tooltip = hotspot.querySelector('.hotspot__tooltip');
    if (!(tooltip instanceof HTMLElement)) return;
    
    // Calculate hotspot position relative to container
    const hotspotCenterX = hotspotRect.left + hotspotRect.width / 2 - containerRect.left;
    const hotspotCenterY = hotspotRect.top + hotspotRect.height / 2 - containerRect.top;
    
    // Get tooltip dimensions (temporarily show it to measure)
    const originalVisibility = tooltip.style.visibility;
    const originalOpacity = tooltip.style.opacity;
    
    tooltip.style.visibility = 'hidden';
    tooltip.style.opacity = '1';
    tooltip.style.display = 'block';
    
    const tooltipRect = tooltip.getBoundingClientRect();
    
    // Restore original styles
    tooltip.style.visibility = originalVisibility;
    tooltip.style.opacity = originalOpacity;
    tooltip.style.display = '';
    
    const tooltipWidth = tooltipRect.width || 230; // fallback to default width
    const tooltipHeight = tooltipRect.height || 100; // estimated height
    
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;
    
    // Margins from container edges
    const edgeMargin = 15;
    
    // Check horizontal positioning (tooltip centered over hotspot by default)
    const tooltipLeftEdge = hotspotCenterX - tooltipWidth / 2;
    const tooltipRightEdge = hotspotCenterX + tooltipWidth / 2;
    
    if (tooltipLeftEdge < edgeMargin) {
      hotspot.classList.add('tooltip-left');
    } else if (tooltipRightEdge > containerWidth - edgeMargin) {
      hotspot.classList.add('tooltip-right');
    }
    
    // Check vertical positioning (tooltip centered over hotspot by default)
    const tooltipTopEdge = hotspotCenterY - tooltipHeight / 2;
    const tooltipBottomEdge = hotspotCenterY + tooltipHeight / 2;
    
    if (tooltipTopEdge < edgeMargin) {
      hotspot.classList.add('tooltip-below');
    } else if (tooltipBottomEdge > containerHeight - edgeMargin) {
      hotspot.classList.add('tooltip-above');
    }
  }

  /**
   * Closes the currently active hotspot
   */
  #closeActiveHotspot() {
    if (this.#activeHotspot) {
      this.#activeHotspot.classList.remove('active');
      this.#activeHotspot.setAttribute('aria-expanded', 'false');
      this.#activeHotspot = null;
    }
  }

  /**
   * Closes all tooltips across all hotspot instances
   */
  #closeAllTooltips() {
    ProductMediaHotspots.#instances.forEach(instance => {
      instance.#closeActiveHotspot();
    });
  }

  /**
   * Closes all hotspots (public method)
   */
  closeAll() {
    this.#closeActiveHotspot();
  }

  /**
   * Static method to close all tooltips globally
   */
  static closeAllTooltips() {
    ProductMediaHotspots.#instances.forEach(instance => {
      instance.#closeActiveHotspot();
    });
  }

  /**
   * Sets up event listeners for close buttons
   */
  #setupCloseButtons() {
    const closeButtons = this.querySelectorAll('.hotspot__close');
    closeButtons.forEach(closeButton => {
      closeButton.addEventListener('click', this.#handleCloseButtonClick);
      closeButton.addEventListener('keydown', this.#handleCloseButtonKeydown);
    });
  }

  /**
   * Handles direct clicks on close buttons
   * @param {Event} event - The click event
   */
  #handleCloseButtonClick = (event) => {
    console.log('Direct close button click'); // Debug log
    event.stopPropagation();
    event.preventDefault();
    
    if (!(event.target instanceof HTMLElement)) return;
    
    const hotspot = event.target.closest('.hotspot');
    if (hotspot && this.#activeHotspot === hotspot) {
      console.log('Closing via direct click'); // Debug log
      this.#closeActiveHotspot();
    }
  };

  /**
   * Handles keyboard events on close buttons
   * @param {KeyboardEvent} event - The keyboard event
   */
  #handleCloseButtonKeydown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#handleCloseButtonClick(event);
    }
  };
}

// Register the custom element
if (!customElements.get('product-media-hotspots')) {
  customElements.define('product-media-hotspots', ProductMediaHotspots);
}
